import { ChangeDetectorRef, Component, effect, ElementRef, EventEmitter, Injectable, Output, output, QueryList, signal, ViewChildren } from '@angular/core';
import { Review } from '../../../../../../state-management/model/interfaces/state.model';
import { WebsiteStateService } from '../../../../../../state-management/services/website-state.service';
import { CommonModule } from '@angular/common';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatIconModule } from '@angular/material/icon';
import { FormsModule } from '@angular/forms';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';

// new imports
import {AfterViewInit, ViewChild} from '@angular/core';
import {MatPaginator, MatPaginatorModule, MatPaginatorSelectConfig, MatPaginatorIntl} from '@angular/material/paginator';
import {MatSort, MatSortModule} from '@angular/material/sort';
import {MatTableDataSource, MatTableModule} from '@angular/material/table';
import {MatInputModule} from '@angular/material/input';
import {MatFormFieldModule} from '@angular/material/form-field';
import { MatButtonModule } from '@angular/material/button';
import { MatSelectModule } from '@angular/material/select';
import { MatBadgeModule } from '@angular/material/badge';

import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatDividerModule } from '@angular/material/divider';

import { EllipsisDirective } from 'ngx-ellipsis';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { ReviewMode } from '../website-config-reviews.component';

export interface ReviewExtended extends Review {
  expanded: boolean;
  isTruncated: boolean;
}

// @Injectable()
// export class CustomMatPaginatorIntl extends MatPaginatorIntl {
//   override itemsPerPageLabel = 'Einträge pro Seite:';
//   override nextPageLabel = 'Nächste Seite';
//   override previousPageLabel = 'Vorherige Seite';
//   override firstPageLabel = 'Erste Seite';
//   override lastPageLabel = 'Letzte Seite';
// }


@Component({
  selector: 'app-website-config-reviews-select',
  standalone: true,
    imports: [
      CommonModule,
      MatExpansionModule,
      FormsModule,
      MatSlideToggleModule,
      MatIconModule,
      MatButtonModule,
      // new imports
      MatFormFieldModule, MatInputModule, MatTableModule, MatSortModule, MatPaginatorModule,
      MatSelectModule, MatBadgeModule, MatCheckboxModule, MatDividerModule, MatButtonToggleModule,
      EllipsisDirective
    ],
    // providers: [
    //   { provide: MatPaginatorIntl, useClass: CustomMatPaginatorIntl }
    // ],
  templateUrl: './website-config-reviews-select.component.html',
  styleUrl: './website-config-reviews-select.component.scss'
})
export class WebsiteConfigReviewsSelectComponent implements AfterViewInit {
  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(MatSort) sort!: MatSort;
  @ViewChild(EllipsisDirective) ellipsisRef!: EllipsisDirective;
  readonly reviewModeChange = output<ReviewMode>();
  readonly ReviewMode = ReviewMode;
  public selectedMode: ReviewMode = ReviewMode.Select;
  public reviewData: Review[] = this.websiteState.configuration().reviews;
  public displayedColumns: string[] = ['name', 'rating', 'text', 'show'];
  public dataSource: MatTableDataSource<ReviewExtended>;
  public showFilters = false;
  public activeFilterCount = 0;
  public ratingOptions = [1, 2, 3, 4, 5];
  public adminTheme = {
    panelClass: 'admin-theme',
    disableOptionCentering: false
  } as MatPaginatorSelectConfig
  public filter: { name: string, rating: Record<number,boolean>, show: boolean | null } = {
    name: '',
    rating: { 1: false, 2: false, 3: false, 4: false, 5: false},
    show: null
  };

  constructor(private websiteState: WebsiteStateService, private cd: ChangeDetectorRef) {
    this.dataSource = new MatTableDataSource(this.augmentReviews(this.reviewData));
    effect(() => {
      const configuration = this.websiteState.configuration();
      this.reviewData = configuration.reviews;
      this.updateDataSource(this.reviewData);
    });
  }
  
  ngAfterViewInit() {
    this.assignPaginatorAndSort();
  }

  private updateDataSource(reviews: Review[]): void {
    this.dataSource = new MatTableDataSource(this.augmentReviews(reviews));
    this.assignPaginatorAndSort();
    this.applyFilter();
  }
  
  private assignPaginatorAndSort(): void {
    if (this.dataSource) {
      this.dataSource.paginator = this.paginator;
      this.dataSource.sort = this.sort;
    }
  }
  
  private augmentReviews(reviews: Review[]): ReviewExtended[] {
    return reviews.map(review => ({
      ...review,
      expanded: false,
      isTruncated: false
    }));
  }
  
  public onReviewModeChange(value: ReviewMode): void {
    this.selectedMode = value;
    this.reviewModeChange.emit(this.selectedMode);
  }

  public toggleExpand(review: ReviewExtended): void {
    review.expanded = !review.expanded;
  }

  public updateReviewShow(show: boolean, id: string): void {
    const updatedReviews = this.reviewData.map(review => {
      if (review.id === id) {
        if (show) {
          const maxPosition = Math.max(
            ...this.reviewData.map(r => (r.websitePosition !== null ? r.websitePosition : 0))
          );
          return { ...review, ...{show, websitePosition: maxPosition + 1} };
        }
        else {
          return { ...review, show, websitePosition: null };
        }
      }
      return { ...review };
    });
    // Positionen anpassen, wenn ein Eintrag auf `false` gesetzt wird
    if (!show) {
      const currentReview = this.reviewData.find(r => r.id === id);
      const currentPosition = currentReview?.websitePosition;

      if (currentPosition !== null && currentPosition !== undefined) {
        updatedReviews.forEach(r => {
          if (r.websitePosition !== null && r.websitePosition > currentPosition) {
            r.websitePosition -= 1;
          }
        });
      }
    }
    // Nach `websitePosition` sortieren
    const sortedReviews = updatedReviews.sort((a, b) => {
      if (a.websitePosition === null) return 1;
      if (b.websitePosition === null) return -1;
      return a.websitePosition - b.websitePosition;
    });
    // Aktualisierte Konfiguration setzen
    this.websiteState.updateConfiguration({
      reviews: sortedReviews,
    });
  }

  public onFilterRatingChange(rating: number, isChecked: boolean): void {
    this.filter.rating[rating] = isChecked;
    this.applyFilter();
  }

  public applyFilter(): void {
    const activeRatings = this.ratingOptions.filter(rating => this.filter.rating[rating]);
    this.dataSource.filterPredicate = (data: Review, filter: string) => {
      const { name, show } = this.filter;
      const matchesName = name ? data.name.toLowerCase().includes(name.toLowerCase()) : true;
      const matchesShow = show !== null ? data.show === show : true;
      const matchesRating = activeRatings.length > 0 ? activeRatings.includes(data.rating) : true;
      this.updateActiveFilterCount();
      return matchesName && matchesRating && matchesShow;
    };
    this.triggerTableFilterUpdate();
  }

  private triggerTableFilterUpdate(): void {
    // Dummy-Filterwert, um das Filter-Trigger auszulösen und die Tabelle zu aktualisieren
    this.dataSource.filter = Math.random().toString();
  }

  private updateActiveFilterCount(): void {
    const showFilterActive = this.filter.show !== null;
    const nameFilterActive = this.filter.name.trim() !== '';
    const activeRatingsCount = Object.values(this.filter.rating).filter(isChecked => isChecked).length;
    this.activeFilterCount = (showFilterActive ? 1 : 0) + (nameFilterActive ? 1 : 0) + activeRatingsCount;
  }

  public resetFilters(): void {
    this.filter = {
      name: '',
      rating: { 1: false, 2: false, 3: false, 4: false, 5: false, },
      show: null,
    };
    this.applyFilter();
  }

  public toggleFilters(): void {
    this.showFilters = !this.showFilters;
  }

  public onTextTruncated(truncated: number, review: ReviewExtended): void {
    review.isTruncated = truncated > 0; // Konvertiere den numerischen Wert in einen boolean
  }

  public showComplete(review: ReviewExtended): void {
    review.expanded = true;
    this.cd.detectChanges();
    this.applyEllipsis();
  }

  public toggleShowMore(review: ReviewExtended): void {
    review.expanded = !review.expanded;
    this.cd.detectChanges();
    this.applyEllipsis();
    // if (!review.expanded) {
    //   this.applyEllipsis();
    // }
  }

  private applyEllipsis(): void {
    this.ellipsisRef.applyEllipsis();
  }
}
